import {
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  Box,
  IconButton,
  Collapse,
  Stack,
  Button,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslations } from '@/i18n/use-translations';

const lightSecondarySwitchSx = (theme: Theme) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    'color': alpha(theme.palette.secondary.main, 0.72),
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.06),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.secondary.main,
    opacity: 0.28,
  },
});

export type MediaSettingsSwitchConfirm = {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
};

type Props = {
  switchKey: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  tooltip?: string;
  onChange: (checked: boolean) => void;
  /** When true, row does not grow — for inline “prefix + switches” toolbars. */
  inline?: boolean;
  /** Optional leading icon (e.g. playlist permission icons). */
  leadingIcon?: ReactNode;
  /** Inline confirm shown directly under this switch row. */
  confirm?: MediaSettingsSwitchConfirm | null;
};

export default function MediaSettingsSwitchRow({
  switchKey,
  label,
  checked,
  disabled,
  tooltip,
  onChange,
  inline = false,
  leadingIcon,
  confirm,
}: Props) {
  const t = useTranslations();
  const confirmIndent = leadingIcon ? 4.5 : 0;

  return (
    <Box sx={{ flex: inline ? '0 0 auto' : '1 1 auto', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: leadingIcon ? 1 : 0.5, minWidth: 0 }}>
        {leadingIcon ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>{leadingIcon}</Box>
        ) : null}
        <FormControlLabel
          sx={{ m: 0, flex: inline ? '0 0 auto' : '1 1 auto', minWidth: 0 }}
          control={
            <Switch
              key={switchKey}
              size="small"
              color="secondary"
              checked={checked}
              disabled={disabled}
              onChange={(_, value) => onChange(value)}
              sx={lightSecondarySwitchSx}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
              {label}
            </Typography>
          }
        />
        {tooltip && (
          <Tooltip title={tooltip}>
            <IconButton
              size="small"
              type="button"
              aria-label={tooltip}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0.25, cursor: 'help' }}
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Collapse in={Boolean(confirm)}>
        <Box sx={{ pl: confirmIndent, pt: 0.75, pb: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: 1.45 }}>
            {confirm?.message}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" color="inherit" onClick={confirm?.onCancel}>
              {t('common.cancel')}
            </Button>
            <Button size="small" variant="contained" onClick={confirm?.onConfirm}>
              {t('common.confirm')}
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
